import React from 'react';
import { Box } from '@mui/material';
import { CustomDonutChart } from '../../../../PDFViewer/components';
import styles from '../69842241581723c403c697fc/PdfPreview/styles';
import {
  PVM_DONUT_COLORS,
  PVM_HEADLINE_COLOR,
  PVM_TEXT_COLOR,
  withPvmBodyStyle,
  withPvmHeadlineStyle,
} from './pvmBranding';
import { dedupeAllocationByLabel, dedupeTop10ByName } from './dedupeGpsAllocations';
import {
  GPS_DEFAULT_ASSET_ALLOCATION,
  GPS_DEFAULT_SECTOR_ALLOCATION,
  GPS_DEFAULT_TOP10_STOCKS,
} from './gpsAllocationDefaults';

export const GPS_DONUT_ASSET_ID = 'gps-donut-asset-allocation';
export const GPS_DONUT_SECTOR_ID = 'gps-donut-sector-allocation';

const DEFAULT_DONUT_COLORS = PVM_DONUT_COLORS;

const formatStockPercentage = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized.endsWith('%') ? normalized.slice(0, -1) : normalized;
  }
  const numValue = Number(value);
  if (isNaN(numValue)) return '';
  return numValue.toFixed(2).replace('.', ',');
};

function resolveAllocationRows(pdf: any, key: 'assetAllocation' | 'sectorAllocation') {
  const top = pdf?.[key];
  const fromAdditional = pdf?.additional_fields?.[key];
  const resolved = dedupeAllocationByLabel(
    (Array.isArray(top) && top.length ? top : fromAdditional) || [],
  );
  if (resolved.length) return resolved;
  return dedupeAllocationByLabel(
    key === 'assetAllocation' ? GPS_DEFAULT_ASSET_ALLOCATION : GPS_DEFAULT_SECTOR_ALLOCATION,
  );
}

function resolveTop10Rows(pdf: any) {
  const top = pdf?.top10Stocks;
  const fromAdditional = pdf?.additional_fields?.top10Stocks;
  const resolved = dedupeTop10ByName((Array.isArray(top) && top.length ? top : fromAdditional) || []);
  if (resolved.length) return resolved;
  return dedupeTop10ByName(GPS_DEFAULT_TOP10_STOCKS);
}

export interface GpsPage2RightColumnProps {
  pdf: any;
  sectionHeaders: any;
  colorSets?: string[];
  handleEditableItemClick?: () => void;
  compactLayout?: boolean;
  /** Tighter title margins so the first section aligns with the left image top. */
  sectionTitleSx?: (theme: Parameters<typeof styles.contentTitle>[0]) => object;
}

export const GpsPage2RightColumn: React.FC<GpsPage2RightColumnProps> = ({
  pdf,
  sectionHeaders,
  colorSets = [],
  handleEditableItemClick,
  compactLayout,
  sectionTitleSx: sectionTitleSxProp,
}) => {
  const assetAllocation = resolveAllocationRows(pdf, 'assetAllocation');
  const sectorAllocation = resolveAllocationRows(pdf, 'sectorAllocation');
  const top10Stocks = resolveTop10Rows(pdf);
  const chartMb = compactLayout ? '8px' : '10px';
  const donutColors = colorSets.length > 0 ? colorSets : DEFAULT_DONUT_COLORS;
  const sectionTitleSx =
    sectionTitleSxProp ||
    ((theme: Parameters<typeof styles.contentTitle>[0]) =>
      withPvmHeadlineStyle(styles.contentTitle(theme) as Record<string, unknown>, true));
  const stockTextSx = withPvmBodyStyle({ fontSize: 11 }, true);
  const top10Title = sectionHeaders?.top10Stocks || 'TOP-10 AKTIEN (in %)';

  return (
    <Box sx={{ width: '100%', minWidth: 0, mt: 0, pt: 0 }}>
      {assetAllocation.length > 0 && (
        <Box id="asset-allocation-section" sx={{ mb: chartMb, mt: 0 }}>
          <Box sx={sectionTitleSx} onClick={handleEditableItemClick}>
            {sectionHeaders?.assetAllocation || 'VERMÖGENSAUFTEILUNG'}
          </Box>
          <CustomDonutChart
            data={assetAllocation}
            id={GPS_DONUT_ASSET_ID}
            colors={donutColors}
            chartWidth={compactLayout ? 120 : 130}
            showSegmentGaps
            compact
          />
        </Box>
      )}

      {sectorAllocation.length > 0 && (
        <Box id="sector-allocation-section" sx={{ mb: chartMb }}>
          <Box sx={sectionTitleSx} onClick={handleEditableItemClick}>
            {sectionHeaders?.sectorAllocation || 'BRANCHEN (AKTIENANTEIL)'}
          </Box>
          <CustomDonutChart
            data={sectorAllocation}
            id={GPS_DONUT_SECTOR_ID}
            colors={donutColors}
            chartWidth={compactLayout ? 120 : 130}
            showSegmentGaps
            compact
          />
        </Box>
      )}

      {top10Stocks.length > 0 && (
        <Box id="top10-stocks-section" sx={{ width: '100%', maxWidth: '100%', minWidth: 0, mt: 0.25 }}>
          {/* Use sx={fn} so MUI injects theme — do not call sectionTitleSx({}) */}
          <Box
            sx={[
              sectionTitleSx,
              {
                mb: 0.5,
                pb: 0.25,
                borderBottom: `2px solid ${PVM_HEADLINE_COLOR}`,
              },
            ]}
            onClick={handleEditableItemClick}
          >
            {top10Title}
          </Box>
          <Box sx={{ backgroundColor: '#D2D2D4', overflow: 'hidden', width: '100%' }}>
            {top10Stocks.map((stock, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  px: 0.75,
                  py: 0.2,
                  // Same white split as pie-chart legend rows
                  borderBottom: index < top10Stocks.length - 1 ? '2px solid #ffffff' : 'none',
                }}
              >
                <Box sx={{ width: 20, ...stockTextSx, flexShrink: 0, color: PVM_TEXT_COLOR }}>
                  {index + 1}.
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    ...stockTextSx,
                    pr: 1,
                    minWidth: 0,
                    wordBreak: 'break-word',
                    color: PVM_TEXT_COLOR,
                  }}
                >
                  {stock.name}
                </Box>
                <Box
                  sx={{
                    ...stockTextSx,
                    textAlign: 'right',
                    flexShrink: 0,
                    minWidth: 44,
                    color: PVM_TEXT_COLOR,
                  }}
                >
                  {formatStockPercentage(stock.percentage)}%
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
